import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Tag, Clock, Car } from 'lucide-react';
import type { EventItem } from '../../types/event';
import { WalkIcon, BookIcon, BikeIcon } from '../icons';
import { BaseDetailCard } from '../shared/BaseDetailCard';
import { useAutoTranslation } from '../../hooks/useAutoTranslation';
import { useImageDisplay } from '../../hooks/useImageDisplay';
import { isEmptyField } from '../../utils/timeUtils';
import { logger } from '../../lib/logger';
import { formatPhoneNumber } from '../../utils/formatters';
import { formatTravelTime, formatTimeRange } from '../../utils/formatDuration';
import { favoritesService } from '../../lib/favorites/favoritesService';
import { useSection } from '../../hooks/useSection';
import { analyticsService } from '../../lib/analytics/analyticsService';

interface DetailCardProps {
  item: EventItem;
  items: EventItem[];
  onClose: () => void;
  onItemChange: (item: EventItem) => void;
  onCategorySelect: (category: string) => void;
  onTypeSelect: (type: string) => void;
  config: any;
  translations: Record<string, Record<string, string>>;
  tableId: string;
  categoryColors: Map<string, string>;
  behavior?: 'default' | 'pin' | 'place';
  showIndex?: number | null;
  place?: any;
  onShowPlace?: (place: any) => void;
}

export const DetailCard: React.FC<DetailCardProps> = ({
  item,
  items,
  onClose,
  onItemChange,
  onCategorySelect,
  onTypeSelect,
  config,
  translations,
  tableId,
  categoryColors,
  behavior,
  showIndex,
  place,
  onShowPlace
}) => {
  const { i18n } = useTranslation();
  const section = useSection();
  const [isFavorite, setIsFavorite] = useState(() => favoritesService.isFavorite(item.id));
  const [translatedDescriptionState, setTranslatedDescriptionState] = useState<string | null>(null);
  const [translatedOfferState, setTranslatedOfferState] = useState<string | null>(null);
  
  // Get image URL from image_metadata.url if available
  const imageUrl = item.image_metadata?.url || item.image;
  const { displayImage, shouldDisplay } = useImageDisplay(imageUrl, 'detailCard');

  // Log when item has an offer
  useEffect(() => {
    if (item.offer) {
      logger.debug('Event item has offer', {
        category: 'Offer',
        data: {
          itemId: item.id,
          itemName: item.name,
          offerLength: item.offer.length,
          offerPreview: item.offer.substring(0, 50) + (item.offer.length > 50 ? '...' : ''),
          hasOffers: !!item.offers,
          offersLanguages: item.offers ? Object.keys(item.offers) : []
        }
      });
    }
  }, [item.id, item.name, item.offer, item.offers]);

  // Utiliser le hook useAutoTranslation pour la description
  const { 
    translate: translateDescription, 
    translating: translatingDescription, 
    error: descriptionError 
  } = useAutoTranslation({
    description: item.description,
    currentLanguage: i18n.language,
    context: {
      tableId,
      itemId: item.id,
      idShow: item.idShow || item.idshow,
      sectionType: 'event',
      contentType: 'description'
    }
  });

  // Utiliser le hook useAutoTranslation pour l'offre si elle existe
  const { 
    translate: translateOffer, 
    translating: translatingOffer, 
    error: offerError 
  } = useAutoTranslation({
    description: item.offer || '',
    currentLanguage: i18n.language,
    context: {
      tableId,
      itemId: item.id,
      idShow: item.idShow || item.idshow,
      sectionType: 'event',
      contentType: 'offer'
    }
  });

  // Trouver les autres dates pour le même spectacle
  const otherDates = React.useMemo(() => {
    if (!item.idShow && !item.idshow) return [];
    const idShow = item.idShow || item.idshow;
    return items
      .filter(event => 
        (event.idShow === idShow || event.idshow === idShow) && 
        event.id !== item.id
      )
      .sort((a, b) => {
        const dateCompare = a.date.localeCompare(b.date);
        return dateCompare !== 0 ? dateCompare : a.hour.localeCompare(b.hour);
      });
  }, [items, item]);

  // Effectuer la traduction de la description quand la langue change
  useEffect(() => {
    const checkAndTranslateDescription = async () => {
      const currentLang = i18n.language;
      
      // Vérifier si la traduction est manquante ou vide
      const currentDescription = item.descriptions?.[currentLang];
      const isDescriptionMissing = !currentDescription || 
                               currentDescription.trim() === '' || 
                               currentDescription === '\n';

      if (isDescriptionMissing) {
        const translated = await translateDescription();
        if (translated) {
          // Mettre à jour les descriptions de l'item
          if (!item.descriptions) {
            item.descriptions = {};
          }
          item.descriptions[currentLang] = translated;
          setTranslatedDescriptionState(translated);
          
          logger.debug('Description translation completed for event item', {
            category: 'Translation',
            data: { 
              itemId: item.id,
              itemName: item.name,
              language: currentLang,
              contentType: 'description',
              translatedLength: translated.length
            }
          });
        }
      }
    };

    checkAndTranslateDescription();
  }, [item.descriptions, i18n.language, translateDescription, item.id, item.name]);

  // Effectuer la traduction de l'offre quand la langue change (si elle existe)
  useEffect(() => {
    const checkAndTranslateOffer = async () => {
      // Skip if no offer exists
      if (!item.offer) return;
      
      const currentLang = i18n.language;
      
      // Initialize offers object if it doesn't exist
      if (!item.offers) {
        item.offers = {};
      }
      
      // Vérifier si la traduction est manquante ou vide
      const currentOffer = item.offers[currentLang];
      const isOfferMissing = !currentOffer || 
                          currentOffer.trim() === '' || 
                          currentOffer === '\n';

      if (isOfferMissing) {
        logger.debug('Offer translation needed for event item', {
          category: 'Translation',
          data: { 
            itemId: item.id,
            itemName: item.name,
            language: currentLang,
            contentType: 'offer',
            offerLength: item.offer.length,
            offerPreview: item.offer.substring(0, 50) + (item.offer.length > 50 ? '...' : '')
          }
        });
        
        const translated = await translateOffer();
        if (translated) {
          // Mettre à jour les offres de l'item
          item.offers[currentLang] = translated;
          setTranslatedOfferState(translated);
          
          logger.debug('Offer translation completed for event item', {
            category: 'Translation',
            data: { 
              itemId: item.id,
              itemName: item.name,
              language: currentLang,
              contentType: 'offer',
              translatedLength: translated.length
            }
          });
        }
      }
    };

    if (item.offer) {
      checkAndTranslateOffer();
    }
  }, [item.offers, item.offer, i18n.language, translateOffer, item.id, item.name]);

  const getTranslatedText = (key: string): string => {
    const translation = translations[key];
    if (!translation) return key;
    return translation[i18n.language] || translation.fr || key;
  };

  const getTranslatedCategory = (category: string): string => {
    const translation = translations[category];
    if (!translation) return category;
    return translation[i18n.language] || translation.fr || category;
  };

  const renderContent = (item: EventItem) => (
    <div className="flex flex-col gap-1 text-sm text-gray-600 mb-2">
      {/* Ligne 2: Place (sans icone) */}
      <div className="w-full">
        {item.place}
      </div>
      
      {/* Ligne 3: fulladress + telephone (sans icone) */}
      <div className="w-full">
        {item.fulladress && <span>{item.fulladress}</span>}
        {item.fulladress && item.telephone && <span> • </span>}
        {item.telephone && <span>{formatPhoneNumber(item.telephone)}</span>}
      </div>
      
      {/* Ligne 4: Distance + all three travel times */}
      <div className="flex flex-wrap gap-2">
        {/* Distance */}
        {!isEmptyField(item.distance) && (
          <div className="flex items-center">
            <MapPin size={15} className="mr-1" />
            <span>{item.distance}km</span>
          </div>
        )}
        
        {/* Show walk time */}
        {!isEmptyField(item.timewalk) && (
          <div className="flex items-center">
            <WalkIcon size={15} className="mr-1" />
            <span>{formatTravelTime(item.timewalk, i18n.language)}</span>
          </div>
        )}
        
        {/* Show bike time */}
        {!isEmptyField(item.timebike) && (
          <div className="flex items-center">
            <BikeIcon size={15} className="mr-1" />
            <span>{formatTravelTime(item.timebike, i18n.language)}</span>
          </div>
        )}
        
        {/* Show drive time */}
        {!isEmptyField(item.timedrive) && (
          <div className="flex items-center">
            <Car size={15} className="mr-1" fill="currentColor" />
            <span>{formatTravelTime(item.timedrive, i18n.language)}</span>
          </div>
        )}
      </div>
      
      {/* Ligne 5: Date + hour + price */}
      <div className="flex flex-wrap gap-2 items-center">
        {item.date && (
          <div className="flex items-center">
            <Clock size={15} className="mr-1" />
            <span>
              {new Intl.DateTimeFormat(i18n.language, {
                weekday: 'short',
                day: 'numeric',
                month: 'short'
              }).format(new Date(item.date))}
              {item.hour && item.end_time ? (
                ` ${formatTimeRange(item.hour, item.end_time, i18n.language)}`
              ) : item.hour && item.duration ? (
                ` - ${new Intl.DateTimeFormat(i18n.language, {
                  hour: 'numeric',
                  minute: 'numeric',
                  hour12: i18n.language === 'en'
                }).format(new Date(`2000-01-01T${item.hour}`))} (${formatTravelTime(item.duration, i18n.language)})`
              ) : item.hour ? (
                ` - ${new Intl.DateTimeFormat(i18n.language, {
                  hour: 'numeric',
                  minute: 'numeric',
                  hour12: i18n.language === 'en'
                }).format(new Date(`2000-01-01T${item.hour}`))}`
              ) : ''}
            </span>
          </div>
        )}
        
        {item.price && (
          <div 
            className="flex items-center"
            style={{ color: config['major-color'] }}
          >
            <Tag size={15} className="mr-1" />
            <span>{item.price}</span>
          </div>
        )}
      </div>
    </div>
  );

  const renderBookingButton = (item: EventItem) => {
    if (!item.booking) return null;
    
    return (
      <a
        href={item.booking}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-center px-4 py-2 rounded-lg text-white mb-2"
        style={{ backgroundColor: config['major-color'] }}
        onClick={() => {
          analyticsService.trackItemAction(
            section.id,
            item.id,
            'booking_click',
            'event',
            {
              item_name: item.name,
              url: item.booking,
              section_name: section.names.fr || 'Unknown Section',
              section_type: 'event'
            }
          );
        }}
      >
        <BookIcon size={18} className="mr-2" />
        {getTranslatedText('btn_booking')}
      </a>
    );
  };

  const handleFavoriteToggle = async (newState: boolean) => {
    try {
      if (newState) {
        favoritesService.addFavorite({
          id: item.id,
          sourceType: 'event',
          data: {
            ...item,
            image: imageUrl // Utiliser l'URL d'image correcte
          },
          metadata: {
            sourceSection: section,
            sectionSlug: section.names.fr,
            sectionNames: section.names,
            item_id: item.id,
            section_id: section.id,
            idShow: item.idShow || item.idshow
          }
        });
        
        analyticsService.trackItemAction(
          section.id,
          item.id,
          'add_favorite',
          'event',
          {
            item_name: item.name,
            category: item.category,
            type: item.type,
            section_name: section.names.fr || 'Unknown Section',
            section_type: 'event'
          }
        );
      } else {
        favoritesService.removeFavorite(item.id);
        
        analyticsService.trackItemAction(
          section.id,
          item.id,
          'remove_favorite',
          'event',
          {
            item_name: item.name,
            category: item.category,
            type: item.type,
            section_name: section.names.fr || 'Unknown Section',
            section_type: 'event'
          }
        );
      }
      setIsFavorite(newState);
    } catch (error) {
      logger.error('Error toggling favorite', {
        category: 'Favorites',
        data: { error, itemId: item.id }
      });
    }
  };

  // Construire l'URL d'itinéraire depuis l'hôtel
  const getHotelItineraryUrl = (item: EventItem): string => {
    if (!item.fulladress || !config.fulladress) return '';

    // Encoder les adresses pour l'URL
    const origin = encodeURIComponent(config.fulladress);
    const destination = encodeURIComponent(item.fulladress);

    // Construire l'URL avec les coordonnées de l'hôtel comme origine
    return `https://www.google.com/maps/dir/${origin}/${destination}`;
  };

  // Obtenir la description à afficher
  const getDescription = () => {
    const currentLang = i18n.language;

    if (translatedDescriptionState) return translatedDescriptionState;
    if (item.descriptions?.[currentLang]) return item.descriptions[currentLang];
    return item.description;
  };

  // Obtenir l'offre à afficher
  const getOffer = () => {
    const currentLang = i18n.language;

    if (translatedOfferState) return translatedOfferState;
    if (item.offers?.[currentLang]) return item.offers[currentLang];
    return item.offer;
  };

  return (
    <BaseDetailCard
      item={item}
      items={items}
      onClose={onClose}
      onItemChange={onItemChange}
      config={config}
      translations={translations}
      tableId={tableId}
      renderContent={renderContent}
      renderBookingButton={renderBookingButton}
      onCategorySelect={onCategorySelect}
      onTypeSelect={onTypeSelect}
      categoryColors={categoryColors}
      getTranslatedCategory={getTranslatedCategory}
      getTranslatedText={getTranslatedText}
      itineraryUrl={getHotelItineraryUrl(item)}
      description={getDescription()}
      offer={getOffer()}
      translating={translatingDescription}
      translationError={descriptionError}
      context={{
        sectionType: 'event',
        itemId: item.id,
        idShow: item.idShow || item.idshow,
        contentType: 'description'
      }}
      isFavorite={isFavorite}
      onFavoriteToggle={handleFavoriteToggle}
      image={shouldDisplay ? displayImage : undefined}
      otherDates={otherDates}
      onOtherDateClick={onItemChange}
      behavior={behavior}
      showIndex={showIndex}
      place={place}
      onShowPlace={onShowPlace}
    />
  );
};

export default DetailCard;